import ThumbsUp from '@public/svg/thumbs-up.svg?component';
import clsx from 'clsx';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import { Alter25Badge } from '@/components/alter25/Alter25Badge';
import { ContentfulTextContainer } from '@/components/ContentfulTextContainer';
import FiveStars from '@/components/FiveStars';
import { CircleCheck } from '@/components/icons/CircleCheck';
import { useYotpoReviewsContext } from '@/context/YotpoContext';
import type { YotpoReview } from '@/pages/api/yotpo';

enum ORDER {
  DESC = 'desc',
  ASC = 'asc',
}

const Review = ({ review, index }: { review: YotpoReview; index: number }) => {
  const { name, title, content, updated_at, score } = review;
  return (
    <div
      className={clsx(
        index > 0 && 'border-t border-alter-black-10',
        'flex flex-col gap-7 pt-6 md:flex-row'
      )}
    >
      <div className="space-y-2 basis-1/3">
        <div className="mb-1">{name}</div>
        <Alter25Badge
          darkText="Verified"
          lightText="Owner"
          icon={<CircleCheck className="text-checkmark-green w-[18px] h-[18px]" />}
        />
        {score > 3.5 && (
          <div className="flex gap-2 text-alter-black-80 pt-1 font-light tracking-[-0.48px]">
            <ThumbsUp />I recommend this product
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3 basis-2/3">
        <div className="flex justify-between">
          <FiveStars score={score} />
          <span className="text-alter-black-60">
            {new Date(updated_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
        </div>

        {title && <ContentfulTextContainer markdown={title} className="mt-1 -mb-6" />}
        <ContentfulTextContainer
          markdown={content}
          className="mt-1 font-light tracking-[-0.48px]"
        />
      </div>
    </div>
  );
};

interface YotpoReviews2Props {
  className?: string;
}

export const YotpoReviews2: React.FC<YotpoReviews2Props> = ({ className }) => {
  if (!className) {
    className = 'bg-white';
  }

  const { yotpoResponse } = useYotpoReviewsContext();

  const [order] = useState<ORDER>(ORDER.DESC);
  const [debouncedSearch] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 6;

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      setSearch(debouncedSearch);
    }, 250);

    return () => {
      clearTimeout(debounce);
    };
  }, [search, debouncedSearch]);

  const filteredAndSortedReviews = React.useMemo(() => {
    if (!yotpoResponse) return [];
    return yotpoResponse.reviews
      .filter((review) => review.content.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        if (order === ORDER.DESC) {
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        } else {
          return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
        }
      });
  }, [yotpoResponse, search, order]);

  const currentReviews = filteredAndSortedReviews.slice(
    (currentPage - 1) * reviewsPerPage,
    currentPage * reviewsPerPage
  );

  const totalPages = Math.ceil(filteredAndSortedReviews.length / reviewsPerPage);

  return (
    <section id="yotpo-reviews" className={clsx('py-16 px-4 md:rounded-none md:px-20', className)}>
      <div className="width-1440 text-alter-black">
        <div className="flex justify-center items-center flex-col">
          <h3
            className={clsx(
              'font-light',
              'mt-4 text-[24px] leading-[28.8px] tracking-[-0.72px]',
              'md:mt-6 md:text-[40px] md:leading-[48px] md:tracking-[-1.2px]'
            )}
          >
            What our members are saying
          </h3>

          <div className="flex items-center gap-[10px] mt-4">
            <span className="text-[32px]">{yotpoResponse?.average_score}</span>
            <FiveStars score={yotpoResponse?.average_score} />
          </div>

          <div className="mt-2 text-alter-black-60 font-light">
            Based on {yotpoResponse?.total_reviews} reviews &bull;{' '}
            {((yotpoResponse.average_score / 5) * 100).toFixed(0)}% would recommend this product
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="disabled:opacity-50"
            aria-label="Previous"
          >
            <Image
              src="/icons/chev-right.svg"
              alt="Previous"
              width={12}
              height={12}
              className="rotate-180"
            />
          </button>
          <span>
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="disabled:opacity-50"
            aria-label="Next"
          >
            <Image src="/icons/chev-right.svg" alt="Next" width={12} height={12} />
          </button>
        </div>

        <div className="mt-6">
          {currentReviews.map((review, index) => (
            <Review key={review.id} review={review} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export const YotpoStars = () => {
  const { yotpoResponse } = useYotpoReviewsContext();
  return <FiveStars score={yotpoResponse?.average_score} />;
};
